import type { RingFinish } from '@jouzen/ecom-components/v3/types/ringFinish';

import type { GetStyleStockStatus } from '@/app/[locale]/product/_hooks/useProductsInventory';
import { SELECT_SIZE, SIZING_KIT } from '@/app/[locale]/store/_consts';
import {
  GEN3_POP_PATHNAME,
  STORE_ACCESSORIES_ROOT_PATH,
} from '@/app/consts/pathnames';
import { ChargerModelEnum } from '@/app/enums/ChargerModelEnum';
import { type RingStyleEnum } from '@/app/enums/RingStyleEnum';
import type { RingStyle } from '@/app/types/ring';

import type CurrentRouteValues from '../_hooks/useCurrentRoute/types';

interface CheckCurrentRouteReturn {
  isValid: boolean;
  redirectUrl: string | null;
}

interface CheckCurrentRouteProps extends CurrentRouteValues {
  getFirstStockedFinish: (props: GetStyleStockStatus) => RingFinish;
  getFirstStockedStyle: () => RingStyle;
  isSizesOnly: boolean;
}

interface RouteValue {
  isValid: boolean;
  value: string;
}

const _checkAccessoryRoutes = (
  accessory: RouteValue,
  size: RouteValue,
): CheckCurrentRouteReturn => {
  if (accessory.value.length && !accessory.isValid) {
    return {
      isValid: false,
      redirectUrl: `${STORE_ACCESSORIES_ROOT_PATH}/${ChargerModelEnum.ChargerOura4}`,
    };
  }

  if (size.value.length && !size.isValid && accessory.isValid) {
    return {
      isValid: false,
      redirectUrl: `${STORE_ACCESSORIES_ROOT_PATH}/${accessory.value}/${SELECT_SIZE}`,
    };
  }

  return {
    isValid: true,
    redirectUrl: null,
  };
};

const _checkRingsRoutes = ({
  finish,
  getFirstStockedFinish,
  getFirstStockedStyle,
  isSizesOnly,
  size,
  style,
}: Pick<
  CheckCurrentRouteProps,
  | 'finish'
  | 'getFirstStockedFinish'
  | 'getFirstStockedStyle'
  | 'isSizesOnly'
  | 'size'
  | 'style'
>): CheckCurrentRouteReturn => {
  const firstStockedStyle = getFirstStockedStyle();

  const firstStockedFinish = getFirstStockedFinish({
    style: style.value as RingStyleEnum,
  });

  if (style.value.length && !style.isValid) {
    return {
      isValid: false,
      redirectUrl: `${GEN3_POP_PATHNAME}/${firstStockedStyle}`,
    };
  }

  if (finish.value.length && !finish.isValid) {
    return {
      isValid: false,
      redirectUrl: `${GEN3_POP_PATHNAME}/${style.value}/${firstStockedFinish}`,
    };
  }

  if (size.value.length && !size.isValid && !isSizesOnly) {
    return {
      isValid: false,
      redirectUrl: `${GEN3_POP_PATHNAME}/${style.value}/${finish.value}/${SIZING_KIT}`,
    };
  }

  if (size.value.length && size.value === SIZING_KIT && isSizesOnly) {
    return {
      isValid: false,
      redirectUrl: `${GEN3_POP_PATHNAME}/${style.value}/${finish.value}/${SELECT_SIZE}`,
    };
  }
  return {
    isValid: true,
    redirectUrl: null,
  };
};

const checkCurrentRoute = ({
  accessory,
  finish,
  getFirstStockedFinish,
  getFirstStockedStyle,
  isAccessory,
  size,
  style,
  isSizesOnly,
}: CheckCurrentRouteProps): CheckCurrentRouteReturn => {
  if (isAccessory) {
    return _checkAccessoryRoutes(accessory, size);
  } else {
    return _checkRingsRoutes({
      finish,
      getFirstStockedFinish,
      getFirstStockedStyle,
      isSizesOnly,
      size,
      style,
    });
  }
};

export default checkCurrentRoute;
